import type { ManifestScreenGroup, ManifestVideoWall } from '../types';

export interface SyncCommand {
  type: 'PLAY' | 'PAUSE' | 'SEEK' | 'SYNC_TIME';
  timestamp: number; // When to execute the command
  payload?: {
    assetId?: string;
    playlistId?: string;
    position?: number; // Seek position in ms
    syncTime?: number; // Reference time for sync
  };
}

export interface SyncState {
  isInitialized: boolean;
  isMaster: boolean;
  syncMethod: 'none' | 'master' | 'slave' | 'software' | 'hardware_genlock' | 'hardware_sender' | 'ntp';
  connectedPeers: number;
  lastSyncTime: number;
  clockOffset: number; // Difference between local time and reference time
}

type SyncCommandCallback = (command: SyncCommand) => void;

/**
 * SyncManager handles synchronized playback between multiple devices.
 *
 * Supports two scenarios:
 * 1. Video Wall sync - devices showing sliced content need frame-accurate sync
 * 2. Screen Group sync - devices showing same/different content in coordination
 *
 * Sync methods:
 * - none: No synchronization
 * - master: This device broadcasts commands to slaves
 * - slave: This device receives commands from master
 * - software: NTP-based software synchronization
 * - hardware_genlock: External genlock hardware handles sync
 * - hardware_sender: LED sender card handles sync (Novastar, Colorlight, etc.)
 * - ntp: NTP server-based precise time sync
 */
class SyncManager {
  private state: SyncState = {
    isInitialized: false,
    isMaster: false,
    syncMethod: 'none',
    connectedPeers: 0,
    lastSyncTime: 0,
    clockOffset: 0,
  };

  private videoWallConfig: ManifestVideoWall | null = null;
  private screenGroupConfig: ManifestScreenGroup | null = null;
  private commandCallback: SyncCommandCallback | null = null;
  private ntpSyncInterval: ReturnType<typeof setInterval> | null = null;
  private wsConnection: WebSocket | null = null;
  private wsMessageHandler: ((event: MessageEvent) => void) | null = null;

  /**
   * Initialize sync manager with video wall configuration
   */
  initializeForVideoWall(config: ManifestVideoWall): void {
    this.videoWallConfig = config;
    this.screenGroupConfig = null;

    // Determine if this device is the master (position 0,0)
    const isMaster = config.row === 0 && config.column === 0;

    this.state = {
      isInitialized: true,
      isMaster,
      syncMethod: config.syncMethod,
      connectedPeers: 0,
      lastSyncTime: Date.now(),
      clockOffset: 0,
    };

    console.log('[SyncManager] Initialized for video wall');
    console.log('[SyncManager] Mode:', isMaster ? 'MASTER' : 'SLAVE');
    console.log('[SyncManager] Sync method:', config.syncMethod);

    // Start NTP sync if using NTP method
    if (config.syncMethod === 'ntp' || config.syncMethod === 'software') {
      this.startNtpSync();
    }
  }

  /**
   * Initialize sync manager with screen group configuration
   */
  initializeForScreenGroup(config: ManifestScreenGroup): void {
    this.screenGroupConfig = config;
    this.videoWallConfig = null;

    const isMaster = config.syncMethod === 'master';

    this.state = {
      isInitialized: true,
      isMaster,
      syncMethod: config.syncMethod,
      connectedPeers: 0,
      lastSyncTime: Date.now(),
      clockOffset: 0,
    };

    console.log('[SyncManager] Initialized for screen group');
    console.log('[SyncManager] Mode:', isMaster ? 'MASTER' : config.syncMethod === 'slave' ? 'SLAVE' : 'INDEPENDENT');
  }

  /**
   * Set callback for receiving sync commands
   */
  setCommandCallback(callback: SyncCommandCallback): void {
    this.commandCallback = callback;
  }

  /**
   * Get current sync state
   */
  getState(): SyncState {
    return { ...this.state };
  }

  /**
   * Check if synchronization is enabled
   */
  isSyncEnabled(): boolean {
    return this.state.isInitialized && this.state.syncMethod !== 'none';
  }

  /**
   * Check if this device is the master
   */
  isMaster(): boolean {
    return this.state.isMaster;
  }

  /**
   * Get synchronized time (accounting for clock offset)
   */
  getSyncedTime(): number {
    return Date.now() + this.state.clockOffset;
  }

  /**
   * Calculate when playback should start for synchronization
   * Returns a timestamp that all devices should use to start playback
   */
  calculateSyncedStartTime(delayMs: number = 2000): number {
    const now = this.getSyncedTime();

    // Round to nearest sync boundary based on method
    switch (this.state.syncMethod) {
      case 'ntp':
        // NTP: Round to nearest 100ms boundary
        return Math.ceil((now + delayMs) / 100) * 100;

      case 'software':
        // Software: Round to nearest second boundary
        return Math.ceil((now + delayMs) / 1000) * 1000;

      case 'master':
      case 'slave':
        // Master/slave: Round to nearest 500ms boundary
        return Math.ceil((now + delayMs) / 500) * 500;

      default:
        return now + delayMs;
    }
  }

  /**
   * Broadcast a play command to all slaves (master only)
   */
  broadcastPlay(assetId: string, playlistId: string, startTime?: number): void {
    if (!this.state.isMaster) {
      console.warn('[SyncManager] Only master can broadcast play commands');
      return;
    }

    const command: SyncCommand = {
      type: 'PLAY',
      timestamp: startTime || this.calculateSyncedStartTime(),
      payload: { assetId, playlistId },
    };

    this.broadcastCommand(command);
  }

  /**
   * Broadcast a pause command to all slaves (master only)
   */
  broadcastPause(): void {
    if (!this.state.isMaster) return;

    const command: SyncCommand = {
      type: 'PAUSE',
      timestamp: this.getSyncedTime(),
    };

    this.broadcastCommand(command);
  }

  /**
   * Broadcast a seek command to all slaves (master only)
   */
  broadcastSeek(position: number): void {
    if (!this.state.isMaster) return;

    const command: SyncCommand = {
      type: 'SEEK',
      timestamp: this.calculateSyncedStartTime(500),
      payload: { position },
    };

    this.broadcastCommand(command);
  }

  /**
   * Broadcast a sync time reference to all slaves (master only)
   */
  broadcastSyncTime(): void {
    if (!this.state.isMaster) return;

    const command: SyncCommand = {
      type: 'SYNC_TIME',
      timestamp: this.getSyncedTime(),
      payload: { syncTime: Date.now() },
    };

    this.broadcastCommand(command);
  }

  /**
   * Internal method to broadcast a command via WebSocket
   */
  private broadcastCommand(command: SyncCommand): void {
    if (!this.wsConnection || this.wsConnection.readyState !== WebSocket.OPEN) {
      console.warn('[SyncManager] WebSocket not connected, cannot broadcast');
      return;
    }

    const groupId = this.videoWallConfig?.id || this.screenGroupConfig?.id;

    this.wsConnection.send(JSON.stringify({
      type: 'SYNC_COMMAND',
      groupId,
      command,
    }));

    console.log('[SyncManager] Broadcasted command:', command.type);
  }

  /**
   * Handle incoming sync command from master (slave only)
   */
  handleIncomingCommand(command: SyncCommand): void {
    if (this.state.isMaster) {
      // Masters don't process incoming commands
      return;
    }

    console.log('[SyncManager] Received command:', command.type, 'scheduled for:', new Date(command.timestamp).toISOString());

    // Calculate delay until execution
    const now = this.getSyncedTime();
    const delay = command.timestamp - now;

    if (delay > 0) {
      // Schedule command execution
      setTimeout(() => {
        this.executeCommand(command);
      }, delay);
    } else {
      // Execute immediately (we're late)
      console.warn('[SyncManager] Command execution delayed by', -delay, 'ms');
      this.executeCommand(command);
    }
  }

  /**
   * Execute a sync command
   */
  private executeCommand(command: SyncCommand): void {
    if (this.commandCallback) {
      this.commandCallback(command);
    }

    this.state.lastSyncTime = Date.now();
  }

  /**
   * Wait until synchronized playback should start
   */
  async waitForPlaybackStart(targetTime: number): Promise<void> {
    const now = this.getSyncedTime();
    const delay = targetTime - now;

    if (delay > 0) {
      console.log('[SyncManager] Waiting', delay, 'ms for synchronized start');
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  /**
   * Calculate playback offset to synchronize with target time
   * Used when joining ongoing playback
   */
  calculatePlaybackOffset(playbackStartTime: number, totalDuration: number): number {
    const now = this.getSyncedTime();
    const elapsed = now - playbackStartTime;

    // Handle looped content
    return elapsed % totalDuration;
  }

  /**
   * Start NTP synchronization
   * Periodically sync with NTP server to maintain accurate time
   */
  private startNtpSync(): void {
    // Initial sync
    this.performNtpSync();

    // Periodic sync every 30 seconds
    this.ntpSyncInterval = setInterval(() => {
      this.performNtpSync();
    }, 30000);
  }

  /**
   * Perform NTP time synchronization
   * In production, this would query an NTP server
   */
  /**
   * Perform time synchronization using server time from heartbeat response.
   * Uses the same principle as NTP: measure round-trip time, compute offset.
   * Called externally when a heartbeat response is received.
   */
  updateClockOffset(serverTimeMs: number, requestStartMs: number): void {
    const now = Date.now();

    // Sanity checks — ignore bad data
    if (!serverTimeMs || !requestStartMs || requestStartMs > now) return;
    const roundTripMs = now - requestStartMs;
    if (roundTripMs < 0 || roundTripMs > 30000) return; // RTT > 30s is suspicious

    const oneWayLatency = roundTripMs / 2;
    const offset = serverTimeMs - (requestStartMs + oneWayLatency);
    const rounded = Math.round(offset);

    // Clamp to ±10 minutes — beyond that, the device clock is broken
    // (CLOCK_DRIFT alert handles notification; sync just needs reasonable values)
    const clamped = Math.max(-600000, Math.min(600000, rounded));
    this.state.clockOffset = clamped;

    if (clamped !== rounded) {
      console.warn('[SyncManager] Clock offset clamped from', rounded, 'to', clamped, 'ms');
    } else {
      console.log('[SyncManager] Clock offset updated:', clamped, 'ms (RTT:', roundTripMs, 'ms)');
    }
  }

  private async performNtpSync(): Promise<void> {
    // Clock sync is now driven by heartbeat responses via updateClockOffset().
    // This method is kept for the periodic sync loop but does nothing on its own —
    // the heartbeat fires every ~60s which is frequent enough for video wall sync.
    console.log('[SyncManager] NTP sync check — current offset:', this.state.clockOffset, 'ms');
  }

  /**
   * Connect to WebSocket for peer communication
   */
  connectWebSocket(ws: WebSocket): void {
    // Clean up previous listener if any
    this.disconnectWebSocket();

    this.wsConnection = ws;

    // Create handler reference so we can remove it later
    this.wsMessageHandler = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data);

        if (message.type === 'SYNC_COMMAND') {
          this.handleIncomingCommand(message.command);
        } else if (message.type === 'PEER_COUNT') {
          this.state.connectedPeers = message.count;
        }
      } catch {
        // Not a sync message, ignore
      }
    };

    // Listen for sync-related messages
    ws.addEventListener('message', this.wsMessageHandler);

    // Request peer count
    if (this.state.isMaster) {
      ws.send(JSON.stringify({
        type: 'REQUEST_PEER_COUNT',
        groupId: this.videoWallConfig?.id || this.screenGroupConfig?.id,
      }));
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnectWebSocket(): void {
    if (this.wsConnection && this.wsMessageHandler) {
      this.wsConnection.removeEventListener('message', this.wsMessageHandler);
    }
    this.wsMessageHandler = null;
    this.wsConnection = null;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.ntpSyncInterval) {
      clearInterval(this.ntpSyncInterval);
      this.ntpSyncInterval = null;
    }

    this.disconnectWebSocket();
    this.commandCallback = null;
    this.videoWallConfig = null;
    this.screenGroupConfig = null;

    this.state = {
      isInitialized: false,
      isMaster: false,
      syncMethod: 'none',
      connectedPeers: 0,
      lastSyncTime: 0,
      clockOffset: 0,
    };
  }

  /**
   * Get the current clock offset (local time + offset = server time)
   */
  getClockOffset(): number {
    return this.state.clockOffset;
  }

  /**
   * Get corrected current time (accounting for server clock offset)
   */
  getCorrectedTime(): number {
    return Date.now() + this.state.clockOffset;
  }
}

export const syncManager = new SyncManager();
